import React from "react";
import { needsReviewerFollowUp } from "../hooks/reviewerThreads";
import { useCodiffLinks } from "../hooks/useCodiffLinks";
import { formatProjectName, mrLinkUrl } from "../utils";
import { AltLinkButton } from "./AltLinkButton";
import { ApprovalStatus } from "./ApprovalStatus";
import { Avatar } from "./Avatar";
import { CopyLinkButton } from "./CopyLinkButton";
import { DateTime } from "./DateTime";
import { JiraBadge } from "./JiraBadge";
import { MRComments } from "./MRComments";
import { BotIcon, DraftIcon, MRStatusIcons } from "./MRStatusIcons";
import { PipelineStatus } from "./PipelineStatus";
import { StackChain } from "./StackChain";
import { StackIndicator } from "./StackIndicator";
import { Tooltip } from "./Tooltip";

export const MergeRequest = ({
	mr,
	greyOutBlockingComments,
	isMyOpenMR,
	enableCommentsExpansion = false,
}) => {
	const [useCodiff] = useCodiffLinks();

	if (!mr) return null;

	const highlightMyOpenMR = isMyOpenMR && mr.discussions > 0;

	const stateStyles = {
		opened: "",
		merged:
			"border-l-4 border-l-emerald-600 dark:border-l-emerald-500 monokai:border-l-monokai-green dracula:border-l-dracula-green nord:border-l-nord-green",
		closed:
			"border-l-4 border-l-red-600 dark:border-l-red-500 monokai:border-l-monokai-red dracula:border-l-dracula-red nord:border-l-nord-red",
	};

	const author = mr.author || {};
	const isClosed = mr.state === "merged" || mr.state === "closed";
	const currentUserApproved =
		mr.state === "opened" &&
		["REVIEWED", "REQUESTED_CHANGES", "APPROVED"].includes(mr.myReview);
	const followUp = !isClosed && needsReviewerFollowUp(mr);
	const silent =
		mr.draft ||
		(currentUserApproved && !followUp) ||
		mr.stack?.isBlockedByStack ||
		(greyOutBlockingComments &&
			mr.discussions > 0 &&
			mr.hasHumanReview &&
			!isMyOpenMR &&
			!followUp);

	const iconClass = "w-4 h-4";

	return (
		<div
			data-mr-card={mr.id}
			className={`
        relative
        group
        bg-[var(--color-bg-primary)] rounded-lg shadow p-3 mb-3
        ${silent ? "opacity-60 hover:opacity-100 transition-opacity duration-200" : ""}
        ${mr.stack?.isBlockedByStack ? "ml-4" : ""}
        border border-[var(--color-border)]
        ${currentUserApproved && !followUp ? "border-l-4 border-l-emerald-200 dark:border-l-emerald-900 monokai:border-l-monokai-green/30 dracula:border-l-dracula-green/30 nord:border-l-nord-green/30" : ""}
        ${followUp ? "border-l-4 border-l-sky-600 dark:border-l-sky-400 monokai:border-l-monokai-blue dracula:border-l-dracula-cyan nord:border-l-nord-brightBlue" : ""}
        transition-colors duration-200
        hover:border-[var(--color-border-hover)]
        ${stateStyles[mr.state] || ""}
		${highlightMyOpenMR ? "border-l-4 border-l-yellow-400 dark:border-l-yellow-500 monokai:border-l-monokai-yellow dracula:border-l-dracula-yellow nord:border-l-nord-yellow" : ""}
      `}
		>
			<div className="flex items-start gap-2">
				<Tooltip content={author.name || "Unknown Author"}>
					<Avatar src={author.avatarUrl} alt={author.name} size="md" />
				</Tooltip>

				<div className="flex-1 min-w-0">
					<div className="flex justify-between items-start gap-2">
						<div className="min-w-0">
							<a
								href={mrLinkUrl(mr.webUrl, useCodiff)}
								target="_blank"
								rel="noopener noreferrer"
								className="text-base font-semibold text-[var(--color-link)] hover:underline break-words"
							>
								#{mr.iid}: {mr.title}
							</a>

							<div className="text-sm text-[var(--color-text-secondary)] flex items-center gap-1 flex-wrap">
								<a
									href={mr.webUrl?.split("/-/")[0]}
									target="_blank"
									rel="noopener noreferrer"
									className="hover:underline"
								>
									{formatProjectName(mr.projectPath)}
								</a>
								<JiraBadge mr={mr} />
								<span className="flex items-center">
									<span>
										{author.name}
										<BotIcon author={author} iconClass={iconClass} />
									</span>
									<span>
										• Created <DateTime date={mr.createdAt} />
									</span>
									<span>
										• Updated <DateTime date={mr.updatedAt} />
									</span>
								</span>
							</div>
						</div>

						<div className="flex flex-col items-end gap-1 flex-shrink-0">
							<div className="flex gap-2 items-center">
								<AltLinkButton mr={mr} />
								<CopyLinkButton mr={mr} />
								<MRStatusIcons mr={mr} iconClass={iconClass} />

								{!isClosed && (
									<>
										<PipelineStatus pipeline={mr.headPipeline} />
										<ApprovalStatus
											aiReviewMissing={mr.aiReviewMissing}
											approvers={mr.approvers}
											reviewers={mr.reviewers}
											approvalsLeft={mr.approvalsLeft}
										/>
									</>
								)}

								<DraftIcon draft={mr.draft} iconClass={iconClass} />
							</div>
							<StackIndicator mr={mr} />
						</div>
					</div>
				</div>
			</div>

			<StackChain mr={mr} className="mt-2" />

			{enableCommentsExpansion && (
				<MRComments
					mrId={mr.id}
					className="mt-2 pt-2 border-t border-[var(--color-border)]"
				/>
			)}
		</div>
	);
};

export default MergeRequest;
